import { useCallback, useEffect, useMemo, useState } from "react"

import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch"

import { PropTypes } from "@l2r-front/l2r-proptypes"

import * as Styled from "./ZoomableImage.styled"

export const ZoomableImage = (props) => {
    const {
        src,
        filename,
        ...otherProps
    } = props

    const zoomFactor = 8

    const [container, setContainer] = useState(null)
    const [transformWrapper, setTransformWrapper] = useState(null)

    const [containerWidth, setContainerWidth] = useState(0)
    const [containerHeight, setContainerHeight] = useState(0)

    const [imageNaturalWidth, setImageNaturalWidth] = useState(0)
    const [imageNaturalHeight, setImageNaturalHeight] = useState(0)

    const imageScale = useMemo(() => {
        if (
            containerWidth === 0 ||
            containerHeight === 0 ||
            imageNaturalWidth === 0 ||
            imageNaturalHeight === 0
        ) {
            return 0
        }
        const scale = Math.min(
            containerWidth / imageNaturalWidth,
            containerHeight / imageNaturalHeight,
        )
        return Math.min(scale, 1)
    }, [
        containerWidth,
        containerHeight,
        imageNaturalWidth,
        imageNaturalHeight,
    ])

    const handleResize = useCallback(() => {
        if (container !== null) {
            const rect = container.getBoundingClientRect()
            setContainerWidth(rect.width)
            setContainerHeight(rect.height)
        } else {
            setContainerWidth(0)
            setContainerHeight(0)
        }
    }, [container])

    useEffect(() => {
        handleResize()
        window.addEventListener("resize", handleResize)
        return () => {
            window.removeEventListener("resize", handleResize)
        }
    }, [handleResize])

    useEffect(() => {
        if (transformWrapper) {
            transformWrapper.resetTransform(0)
        }
    }, [imageScale, transformWrapper])

    const handleImageOnLoad = useCallback((image) => {
        setImageNaturalWidth(image.naturalWidth)
        setImageNaturalHeight(image.naturalHeight)
    }, [])

    useEffect(() => {
        const image = new Image()
        image.onload = () => handleImageOnLoad(image)
        image.src = src
    }, [src, handleImageOnLoad])

    return (
        <Styled.Container ref={(el) => setContainer(el)}>
            {imageScale > 0 && (
                <TransformWrapper
                    ref={setTransformWrapper}
                    key={`${containerWidth}x${containerHeight}`}
                    initialScale={imageScale}
                    minScale={imageScale}
                    maxScale={imageScale * zoomFactor}
                    centerOnInit
                    {...otherProps}
                >
                    <TransformComponent
                        wrapperStyle={{
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        <img alt={filename} src={src} />
                    </TransformComponent>
                </TransformWrapper>
            )}
        </Styled.Container>
    )
}

ZoomableImage.propTypes = {
    ...TransformWrapper.propTypes,
    src: PropTypes.string.isRequired,
    filename: PropTypes.string,
}